import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Rocket, Copy, Check, Share2, Users, CreditCard, Euro, Wallet, TrendingUp,
  ArrowLeft, ArrowRight, Loader2, BadgeEuro, Sparkles, Download,
} from "lucide-react";
import { api, vibrate } from "@/lib/api";
import { API } from "@/lib/api";
import { APP_URL, APP_DOMAIN } from "@/config";
import { useAuth } from "@/context/AuthContext";

const fmtE = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return "—"; } };

export default function Creators() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => user && api.get("/creators/me").then((r) => setData(r.data)).catch(() => setData({ is_creator: false }));
  useEffect(() => {
    if (loading) return;
    // Coming back from the creator login flow → clear the intent flag.
    if (user) localStorage.removeItem("dmd_creator_intent");
    load();
    /* eslint-disable-next-line */
  }, [user, loading]);

  const join = async (payload = {}) => {
    if (!user) {
      // Skip the "paste your conversation" onboarding — go straight to login.
      localStorage.setItem("dmd_creator_intent", "1");
      vibrate();
      navigate("/onboarding?creator=1");
      return;
    }
    setBusy(true); vibrate();
    const cp = localStorage.getItem("dmd_cp");
    const com = localStorage.getItem("dmd_com");
    try {
      await api.post("/creators/join", { ...payload, prospect_code: cp || undefined, commercial_code: com || undefined });
      localStorage.removeItem("dmd_cp");
      localStorage.removeItem("dmd_com");
      toast.success("Bienvenue dans le programme 🚀"); await load();
    }
    catch { toast.error("Échec, réessaie"); } finally { setBusy(false); }
  };

  const isCreator = data?.is_creator;

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      <div className="max-w-3xl mx-auto px-5 py-6">
        <button data-testid="creators-back" onClick={() => navigate(user ? "/app" : "/")}
          className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4">
          <ArrowLeft size={16} /> {user ? "Retour à l'app" : "Accueil"}
        </button>

        {!isCreator ? (
          <CreatorPitch onJoin={join} busy={busy} loggedIn={!!user} />
        ) : (
          <CreatorDashboard data={data} reload={load} />
        )}
      </div>
    </div>
  );
}

const SOCIAL_FIELDS = [
  { key: "tiktok", label: "TikTok", placeholder: "@ton_pseudo" },
  { key: "instagram", label: "Instagram", placeholder: "@ton_pseudo" },
  { key: "youtube", label: "YouTube", placeholder: "Ta chaîne" },
  { key: "other", label: "Autre (X, Twitch…)", placeholder: "Lien ou pseudo" },
];

function CreatorPitch({ onJoin, busy, loggedIn }) {
  const [name, setName] = useState("");
  const [audience, setAudience] = useState("");
  const [socials, setSocials] = useState({});
  const setSocial = (k, v) => setSocials((s) => ({ ...s, [k]: v }));

  const rows = [
    { icon: BadgeEuro, t: "50% le 1ᵉʳ mois", d: "sur chaque abonné·e que tu ramènes" },
    { icon: TrendingUp, t: "+ 25% à vie", d: "tant qu'il/elle reste abonné·e — revenus récurrents" },
    { icon: Rocket, t: "Lien & code instantanés", d: "auto-approuvé, tu démarres en 10 secondes" },
    { icon: Wallet, t: "Suivi en temps réel", d: "clics, abonnés, MRR, commissions à recevoir" },
  ];
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="creators-pitch">
      <div className="text-center">
        <div className="text-5xl mb-3">🤝💸</div>
        <p className="text-xs uppercase tracking-[0.25em] text-pink-400 font-bold">Programme Créateurs</p>
        <h1 className="font-display text-3xl sm:text-4xl font-black leading-tight mt-2">
          Parle de DocLove, <span className="text-pink-400">gagne 50%</span>.
        </h1>
        <p className="text-sm text-zinc-400 mt-3 max-w-lg mx-auto">
          TikTokeur·se, UGC, influenceur·se ? Chaque personne qui s'abonne via ton lien te rapporte
          <b className="text-white"> 50% le 1ᵉʳ mois</b> puis <b className="text-white">25% à vie</b>. Zéro plafond.
        </p>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-3">
        {rows.map((r) => (
          <div key={r.t} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-pink-500/15 flex items-center justify-center shrink-0"><r.icon size={17} className="text-pink-400" /></div>
            <div><p className="text-sm font-bold">{r.t}</p><p className="text-xs text-zinc-400">{r.d}</p></div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/25 p-5 text-center">
        <p className="text-sm text-zinc-300">💡 Exemple : 20 abonné·es Pro (14,99€) = <b className="text-white">~150€ le 1ᵉʳ mois</b> + <b className="text-white">~75€/mois récurrents</b>. Et ça grimpe.</p>
      </div>

      {/* Profil créateur — collecté une fois connecté·e */}
      {loggedIn && (
        <div className="mt-6 rounded-2xl bg-zinc-900 border border-zinc-800 p-5 space-y-3" data-testid="creator-profile-form">
          <div>
            <p className="text-sm font-bold">Dis-nous en plus sur toi 👋</p>
            <p className="text-xs text-zinc-500">Pour qu'on te connaisse et adapte tes contenus. Facultatif mais recommandé.</p>
          </div>
          <input
            data-testid="creator-name-input"
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Ton nom / pseudo public"
            className="w-full rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500"
          />
          <div className="grid sm:grid-cols-2 gap-2.5">
            {SOCIAL_FIELDS.map((f) => (
              <input
                key={f.key}
                data-testid={`creator-social-${f.key}`}
                value={socials[f.key] || ""}
                onChange={(e) => setSocial(f.key, e.target.value)}
                placeholder={`${f.label} — ${f.placeholder}`}
                className="w-full rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500"
              />
            ))}
          </div>
          <input
            data-testid="creator-audience-input"
            value={audience} onChange={(e) => setAudience(e.target.value)}
            placeholder="Ton audience totale (ex : 45k abonnés)"
            className="w-full rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500"
          />
        </div>
      )}

      <button data-testid="creators-join" onClick={() => onJoin({ name: name.trim() || undefined, socials, audience: audience.trim() || undefined })} disabled={busy}
        className="mt-6 w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold py-4 hover:opacity-90 active:scale-95 transition-transform shadow-[0_0_28px_rgba(236,72,153,0.4)] disabled:opacity-60">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <>{loggedIn ? "Rejoindre le programme" : "Créer mon compte & rejoindre"} <ArrowRight size={18} /></>}
      </button>
      <p className="text-[11px] text-zinc-600 text-center mt-3">Paiement mensuel dès 50€ de commissions cumulées.</p>
    </motion.div>
  );
}

function CreatorDashboard({ data, reload }) {
  const [copied, setCopied] = useState(false);
  const [payout, setPayout] = useState(data.payout_info || "");
  const [savingP, setSavingP] = useState(false);
  const [socials, setSocials] = useState(data.socials || {});
  const [audience, setAudience] = useState(data.audience || "");
  const [savingS, setSavingS] = useState(false);
  const setSocial = (k, v) => setSocials((s) => ({ ...s, [k]: v }));
  const link = `${APP_URL}/?c=${data.code}`;

  const copy = async () => { try { await navigator.clipboard.writeText(link); } catch {} vibrate(); setCopied(true); toast.success("Lien copié 🎉"); setTimeout(() => setCopied(false), 1500); };
  const share = async () => {
    vibrate();
    const text = `Analyse tes conversations sur DocLove 💘 : ${link}`;
    try { if (navigator.share) await navigator.share({ title: "DocLove", text, url: link }); else { await navigator.clipboard.writeText(text); toast.success("Lien copié"); } }
    catch (e) { if (e?.name !== "AbortError") { await navigator.clipboard.writeText(text); toast.success("Lien copié"); } }
  };
  const savePayout = async () => {
    setSavingP(true); vibrate();
    try { await api.post("/creators/payout-info", { payout_info: payout }); toast.success("Coordonnées enregistrées ✓"); }
    catch { toast.error("Échec"); } finally { setSavingP(false); }
  };
  const saveProfile = async () => {
    setSavingS(true); vibrate();
    try { await api.post("/creators/profile", { socials, audience }); toast.success("Profil mis à jour ✓"); await reload(); }
    catch { toast.error("Échec"); } finally { setSavingS(false); }
  };

  const cards = [
    { label: "Inscriptions", value: data.signups, icon: Users, c: "text-pink-400" },
    { label: "Abonné·es", value: data.paying, icon: CreditCard, c: "text-emerald-400" },
    { label: "MRR généré", value: fmtE(data.mrr), icon: Euro, c: "text-sky-400" },
    { label: "Récurrent / mois", value: fmtE(data.recurring_commission), icon: TrendingUp, c: "text-purple-400" },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="creators-dashboard">
      <div>
        <div className="flex items-center gap-2"><Rocket className="text-pink-400" size={20} /><p className="text-zinc-500 text-sm">Programme Créateurs</p></div>
        <h1 className="font-display text-3xl font-black mt-1">Salut {data.name?.split(" ")[0]} 🚀</h1>
        <p className="text-sm text-zinc-400 mt-1">{data.terms}</p>
      </div>

      {/* Gains */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border border-emerald-500/25 p-4" data-testid="creator-pending">
          <Wallet size={16} className="text-emerald-400" />
          <p className="font-display text-2xl font-black mt-2">{fmtE(data.pending)}</p>
          <p className="text-[11px] uppercase tracking-wider text-zinc-500 mt-0.5">À recevoir</p>
        </div>
        <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
          <Euro size={16} className="text-amber-400" />
          <p className="font-display text-2xl font-black mt-2">{fmtE(data.total_earned)}</p>
          <p className="text-[11px] uppercase tracking-wider text-zinc-500 mt-0.5">Total gagné · {fmtE(data.paid_out)} versé</p>
        </div>
      </div>

      {/* Lien */}
      <div className="neon-border rounded-2xl bg-zinc-900 p-5 space-y-3">
        <p className="text-sm font-semibold">Ton lien d'affiliation</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 rounded-xl bg-zinc-950 border border-zinc-800 px-4 py-3 min-w-0">
            <p className="font-mono text-sm text-pink-300 truncate" data-testid="creator-link">{link}</p>
          </div>
          <button data-testid="creator-copy" onClick={copy} className="rounded-xl bg-zinc-800 hover:bg-zinc-700 px-3 py-3">{copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}</button>
          <button data-testid="creator-share" onClick={share} className="rounded-xl bg-pink-500 hover:bg-pink-600 px-3 py-3 active:scale-95"><Share2 size={16} /></button>
        </div>
        <div className="flex items-center justify-between rounded-xl bg-zinc-950 border border-zinc-800 px-4 py-2.5">
          <span className="text-xs text-zinc-500">Ton code</span><span className="font-mono font-bold tracking-widest text-pink-400">{data.code}</span>
        </div>
        <a href={`${API}/creators/kit.pdf?cp=${data.code}`} target="_blank" rel="noreferrer" data-testid="creator-kit-pdf"
          className="flex items-center justify-center gap-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold py-2.5">
          <Download size={15} /> Télécharger le kit PDF (à partager)
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
            <c.icon size={15} className={c.c} />
            <p className="font-display text-xl font-black mt-2">{c.value}</p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Coordonnées de paiement */}
      <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
        <p className="text-sm font-bold mb-1">💳 Coordonnées de paiement</p>
        <p className="text-xs text-zinc-500 mb-2">PayPal / IBAN — pour recevoir tes commissions (dès 50€ cumulés).</p>
        <div className="flex gap-2">
          <input data-testid="creator-payout-input" value={payout} onChange={(e) => setPayout(e.target.value)} placeholder="PayPal ou IBAN" className="flex-1 rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500" />
          <button data-testid="creator-payout-save" onClick={savePayout} disabled={savingP} className="rounded-xl bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold px-4 disabled:opacity-60">{savingP ? "…" : "OK"}</button>
        </div>
      </div>

      {/* Profil & réseaux */}
      <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4" data-testid="creator-socials-card">
        <p className="text-sm font-bold mb-1">📱 Tes réseaux</p>
        <p className="text-xs text-zinc-500 mb-3">Aide-nous à mieux te connaître et à booster tes contenus.</p>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {SOCIAL_FIELDS.map((f) => (
            <input
              key={f.key}
              data-testid={`creator-dash-social-${f.key}`}
              value={socials[f.key] || ""}
              onChange={(e) => setSocial(f.key, e.target.value)}
              placeholder={`${f.label} — ${f.placeholder}`}
              className="w-full rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500"
            />
          ))}
        </div>
        <input
          data-testid="creator-dash-audience"
          value={audience} onChange={(e) => setAudience(e.target.value)}
          placeholder="Ton audience totale (ex : 45k abonnés)"
          className="w-full mt-2.5 rounded-xl bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm outline-none focus:border-pink-500"
        />
        <button data-testid="creator-socials-save" onClick={saveProfile} disabled={savingS} className="mt-3 w-full rounded-xl bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold py-2.5 disabled:opacity-60">{savingS ? "Enregistrement…" : "Enregistrer mon profil"}</button>
      </div>

      {/* Filleuls */}
      <div>
        <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">Tes filleul·es ({data.signups})</h2>
        {data.referrals?.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-zinc-800 p-8 text-center">
            <Sparkles className="mx-auto text-zinc-600 mb-2" size={20} />
            <p className="text-sm text-zinc-500">Partage ton lien pour lancer la machine 🚀</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="creator-referrals">
            {data.referrals.map((r, i) => (
              <div key={i} className="flex items-center gap-3 rounded-2xl bg-zinc-900 border border-zinc-800 p-3.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500/30 to-purple-500/30 flex items-center justify-center text-pink-300 font-black shrink-0">{(r.name || "?").charAt(0).toUpperCase()}</div>
                <div className="min-w-0 flex-1"><p className="text-sm font-semibold truncate">{r.name}</p><p className="text-xs text-zinc-500">{fmtDate(r.joined_at)} · {r.tier}</p></div>
                {r.paying ? <span className="text-sm font-bold text-emerald-400">{fmtE(r.earned)}</span> : <span className="text-[10px] text-zinc-600">gratuit</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
